/**
 * Session Context - Session State Management & Polling
 *
 * File: Mythbound.App/src/context/SessionContext.tsx
 * Created: 2026-01-06
 * Last Modified: 2026-09-22 (PRD001A 001A.5 — session frontend)
 *
 * PURPOSE:
 * React Context for session lifecycle management (start/end/pause/resume) and
 * state polling. Implements 3-second polling for non-critical updates (HP,
 * inventory, and — as of 001A.5 — the server-computed `can_pause`/
 * `can_resume`/`paused_by_me` flags and pause-excluded elapsed time).
 *
 * WHO: Mounted per-campaign by `App.tsx`'s `CampaignRouteProviders` wrapper,
 * keyed by `campaignId` (see that file) so switching campaigns fully remounts
 * this provider — stopping the old campaign's polling loop and starting fresh.
 * `CampaignPage.tsx`, `SessionControls.tsx`, and `useSessionWebSocket` read/
 * call this context.
 * WHAT: `campaignId` is now an accepted prop (previously only `CharacterProvider`
 * had one — the mismatch was the source of 3 of the recorded 6 baseline
 * TypeScript diagnostics in `CampaignFlow.test.tsx`, which already passed this
 * prop). `status`/`message` replace the previous plain `error: string | null`,
 * mirroring `CharacterContext`. The polling loop now uses a `ref`-held
 * interval (not `useState`) plus an `AbortController` per tick, so a
 * superseded poll response — including one from a still-in-flight request
 * when the campaign changes — is aborted and/or ignored rather than applied.
 * WHERE: src/context/SessionContext.tsx.
 * WHY (PRD001A 001A.4 Wave 3 / 001A.7a): see `CharacterContext.tsx`'s header
 * for the shared rationale (forbidden/unavailable/empty must be distinct
 * states, not a single generic error string).
 * PAUSE/RESUME POLICY (PRD001A 001A.5, real behavior — this context never
 * re-derives it client-side): any verified current campaign participant may
 * pause an active session; only the recorded pauser or the current GM may
 * resume a paused one. This context does not gate the calls themselves — it
 * trusts the server's per-request `can_pause`/`can_resume` response and lets
 * `SessionControls` render its buttons from those flags. A 409 conflict
 * (e.g. someone else already paused it) is mapped to an honest message via
 * `getSessionErrorMessage` and triggers an immediate, unconditional
 * `forceRefresh()` (PRD001A R5a — see that function's own header) so the
 * caller's flags/status are resynced after the conflict, not left stale and
 * never silently dropped by the duplicate-trigger debounce guard.
 *
 * STATE:
 * - session: Session | null
 * - sessionState: SessionState | null
 * - isActive: boolean
 * - status: SessionStatus (see below)
 * - message: string | null
 *
 * ACTIONS:
 * - startSession(campaignId, source)
 * - endSession(summary?)
 * - refreshState()
 * - pauseSession(sessionId) / resumeSession(sessionId) — see policy note above
 *
 * POLLING STRATEGY:
 * - Non-critical updates: 3-second polling (HP changes, inventory, equipment)
 * - Critical events: WebSocket instant (session start/end, participant joined, character created)
 *
 * AUTOSAVE:
 * - Backend autosaves every 5 minutes
 * - 6-minute crash buffer for recovery
 *
 * Pipeline: UI → SessionContext → SessionService → Backend session state
 * Dependencies: React, SessionService, ApiError, CharacterContext for roster context
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect, useRef, ReactNode } from 'react';
import { sessionService, getSessionErrorCode, getSessionErrorMessage, type Session, type SessionState } from '../services/api/SessionService';
import { ApiError } from '../services/api/ApiError';

/**
 * Structured session-state status, replacing the previous `error: string | null`.
 * Shares the same shape as `CharacterContext`'s `CharacterStatus` for a
 * consistent rendering contract in `CampaignPage`, though this context's
 * domain is a single session rather than a list:
 * - `'idle'`: no session started and no state fetch attempted yet.
 * - `'loading'`: a start/end/state fetch is in flight.
 * - `'ready'`: `sessionState` reflects the last successful fetch.
 * - `'empty'`: fetch succeeded but there is no active session to show
 *   (post-`endSession`, or before any session has started this visit).
 * - `'forbidden'`: `ApiError.kind === 'forbidden'` (403) or `'unauthenticated'` (401).
 * - `'not_found'`: `ApiError.kind === 'not_found'` (404).
 * - `'unavailable'`: `ApiError.kind === 'unavailable'` (5xx) or `'network'`.
 * - `'error'`: any other failure.
 */
export type SessionStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'empty'
  | 'forbidden'
  | 'not_found'
  | 'unavailable'
  | 'error';

function statusFromError(err: unknown): { status: SessionStatus; message: string } {
  if (err instanceof ApiError) {
    switch (err.kind) {
      case 'forbidden':
      case 'unauthenticated':
        return { status: 'forbidden', message: err.message };
      case 'not_found':
        return { status: 'not_found', message: err.message };
      case 'unavailable':
      case 'network':
        return { status: 'unavailable', message: err.message };
      default:
        return { status: 'error', message: err.message };
    }
  }
  const message = err instanceof Error ? err.message : 'Something went wrong';
  return { status: 'error', message };
}

/**
 * Build a minimal `Session` identity from a polled `SessionState` (PRD001A
 * R5). `SessionState` (`GET /sessions/{id}/state`) is the only endpoint the
 * existing-session initialization path calls — it never calls `startSession`,
 * which would create a NEW session instead of seeding the one the URL
 * already names. `Session` carries a few fields `SessionState` doesn't
 * (`ended_at`, `started_by`, `duration_minutes`); those are set `null` here
 * rather than fabricated, since nothing in this initialization path has real
 * values for them and no current consumer of `session` (vs. `sessionState`)
 * reads them for an existing session.
 */
function sessionFromState(state: SessionState): Session {
  return {
    session_id: state.session_id,
    campaign_id: state.campaign_id,
    status: state.status,
    started_at: state.started_at ?? null,
    ended_at: null,
    started_by: null,
    duration_minutes: null,
    lifecycle_revision: state.lifecycle_revision,
  };
}

export interface CampaignMutationCursor {
  campaign_id: string;
  current_session_id: string | null;
  is_session_active: boolean;
  /** Campaign projection ordering only; never sent as a session mutation revision. */
  server_revision: number;
}

export interface SessionContextType {
  // State
  session: Session | null;
  sessionState: SessionState | null;
  isActive: boolean;
  status: SessionStatus;
  message: string | null;
  /** Last validated campaign cursor; null means no safe start precondition is known yet. */
  campaignCursor: CampaignMutationCursor | null;
  /** Derived convenience flag (`status === 'loading'`) — kept for existing callers. */
  isLoading: boolean;

  // Actions
  startSession: (campaignId: string, source: 'webapp' | 'discord') => Promise<Session>;
  endSession: (summary?: string) => Promise<Session>;
  refreshState: () => Promise<void>;
  pauseSession: (sessionId: string) => Promise<void>;
  resumeSession: (sessionId: string) => Promise<void>;
  setMasterMode: (sessionId: string, enabled: boolean) => Promise<void>;
  /** Resets `status`/`message` back to `'idle'`. */
  clearError: () => void;
  /**
   * Retries the existing-session initialization effect (PRD001A R5) — a
   * no-op when this provider wasn't given a `sessionId` to initialize from.
   * `SessionRouteProvider` (App.tsx) has its own outer retry for the
   * pre-mount resolve fetch; this is the inner retry for a failure that
   * happens after this provider is already mounted (e.g. a transient
   * network blip on this provider's own seed fetch, or a fetch that fails
   * without `initialState` having been supplied).
   */
  retryInitialization: () => void;
  /** Applies a validated campaign snapshot without allowing an older HTTP poll to overwrite it. */
  applyRealtimeState: (state: SessionState | null) => void;
  applyRealtimeCampaignCursor: (cursor: CampaignMutationCursor | null) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

/**
 * SessionProvider manages session lifecycle (start, active, end)
 *
 * Usage (no existing session — `CampaignPage.tsx`'s "Play Myth" flow calls
 * `startSession` itself):
 * ```tsx
 * <SessionProvider key={campaignId} campaignId={campaignId}>
 *   <CampaignPage />
 * </SessionProvider>
 * ```
 *
 * Usage (initializing an EXISTING session directly from its URL identity,
 * PRD001A R5 — `SessionRouteProvider` in App.tsx does this for `/session/:id`):
 * ```tsx
 * <SessionProvider key={campaignId} campaignId={campaignId} sessionId={sessionId} initialState={alreadyFetchedState}>
 *   <SessionView />
 * </SessionProvider>
 * ```
 * `sessionId`/`initialState` seed `session`/`sessionState` from the real
 * session the URL names and start polling immediately — `startSession` is
 * NEVER called to initialize an existing session (that would create a new
 * one). `initialState` is optional: when supplied and its `session_id`
 * matches `sessionId`, it is used directly to seed synchronously — skipping
 * the extra fetch this provider would otherwise make to re-fetch the exact
 * same state (see `sessionFromState` above). This does NOT mean zero network
 * activity either way: seeding always calls `startPolling()`, which fires
 * its own immediate poll fetch right after seeding (see that function and
 * the initialization effect below) — `initialState` only avoids fetching the
 * SAME state a second time before that poll tick. When there's no matching
 * `initialState`, this provider fetches it itself first, then seeds from
 * that response the same way.
 */
export const SessionProvider: React.FC<{
  children: ReactNode;
  campaignId?: string;
  sessionId?: string;
  initialState?: SessionState;
  realtimeManaged?: boolean;
}> = ({ children, campaignId, sessionId, initialState, realtimeManaged = false }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionState, setSessionState] = useState<SessionState | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [campaignCursor, setCampaignCursor] = useState<CampaignMutationCursor | null>(null);
  const campaignCursorRef = useRef<CampaignMutationCursor | null>(null);

  // Polling: ref-held interval (not useState) so start/stop don't churn on
  // every render, plus a per-tick AbortController and request-generation
  // counter so a stale response (old campaign, or a tick that started before
  // the session ended) is aborted and/or ignored rather than applied.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);
  const pollRequestIdRef = useRef(0);
  const realtimeRevisionRef = useRef(0);
  const lifecycleAbortRef = useRef<AbortController | null>(null);
  const lifecycleRequestIdRef = useRef(0);
  const lifecycleInFlightRef = useRef<{
    kind: 'start' | 'end';
    key: string;
    promise: Promise<Session>;
  } | null>(null);
  const providerIdentityRef = useRef({ campaignId, sessionId });
  providerIdentityRef.current = { campaignId, sessionId };
  // Debounce guard: tracks last fetchState call time (ms since epoch).
  // WHY: pauseSession/resumeSession REST calls trigger refreshState, AND the
  // backend broadcasts session.paused/resumed WebSocket events that also
  // call refreshState via useSessionWebSocket. Without a guard, two fetches
  // fire within milliseconds.
  const lastRefreshRef = useRef<number>(0);

  const stopPolling = useCallback(() => {
    // Invalidate a response even when a fetch implementation ignores abort.
    pollRequestIdRef.current += 1;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
  }, []);

  /**
   * Fetch session state once, with its own AbortController and
   * request-generation guard. Used by both the polling loop and
   * `refreshState`'s manual/debounced trigger, so there is exactly one place
   * that decides whether a resolved fetch is still relevant.
   */
  const fetchState = useCallback(async (sessionId: string, expectedRealtimeRevision?: number) => {
    pollAbortRef.current?.abort();
    const controller = new AbortController();
    pollAbortRef.current = controller;
    const requestId = ++pollRequestIdRef.current;
    const realtimeRevisionAtStart = expectedRealtimeRevision ?? realtimeRevisionRef.current;
    try {
      const state = await sessionService.getSessionState(sessionId, { signal: controller.signal });
      if (requestId !== pollRequestIdRef.current || realtimeRevisionAtStart !== realtimeRevisionRef.current) return; // superseded by a newer request or campaign snapshot
      if (state.session_id !== sessionId) throw new Error('Session state response did not match the requested session');
      setSessionState(state);
      setSession((previous) => previous?.session_id === state.session_id
        ? { ...previous, status: state.status, started_at: state.started_at ?? previous.started_at }
        : sessionFromState(state));
      setIsActive(state.status === 'active' || state.status === 'paused');
      setStatus('ready');
      // Preserve action-specific messages such as a 409 pause conflict while
      // still allowing successful polling to restore readiness.
      // An externally ended session is terminal. Stop the timer-driving poll
      // loop and invalidate any older tick so it cannot restore active state.
      if (state.status === 'ended') stopPolling();
    } catch (err) {
      if (requestId !== pollRequestIdRef.current) return; // superseded
      if (err instanceof DOMException && err.name === 'AbortError') return; // intentionally cancelled
      const { status: nextStatus, message: nextMessage } = statusFromError(err);
      setStatus(nextStatus);
      setMessage(nextMessage);
      console.error('Polling error:', nextMessage);
      // Don't clear session on a poll error — matches previous behavior:
      // a transient/unavailable poll failure shouldn't end the session locally.
    }
  }, [stopPolling]);

  /**
   * Poll session state every 3 seconds for non-critical updates (HP,
   * inventory, equipment). Critical events (start/end/participant join) use
   * WebSocket instant updates instead — see `useSessionWebSocket`.
   */
  const startPolling = useCallback((sessionId: string) => {
    stopPolling();
    void fetchState(sessionId); // initial immediate fetch
    if (!realtimeManaged) {
      pollIntervalRef.current = setInterval(() => {
        void fetchState(sessionId);
      }, 3000);
    }
  }, [fetchState, stopPolling, realtimeManaged]);

  // Retry counter for the existing-session initialization effect below —
  // bumping it forces the effect to re-run even though `sessionId` itself
  // hasn't changed. See `retryInitialization` in the exposed context value.
  const [initRetryToken, setInitRetryToken] = useState(0);
  const retryInitialization = useCallback(() => {
    setInitRetryToken((n) => n + 1);
  }, []);

  /**
   * Initialize an EXISTING session directly from its URL identity (PRD001A
   * R5 — "load/seed the actual URL session identity and current state into
   * the shared provider and start correct polling"). This effect is the fix
   * for the defect the release review described: previously `sessionState`
   * started `null` and polling only began through `startSession()`, so a
   * direct `/session/:id` link rendered with an empty provider — controls
   * and the timer had nothing real to read until something happened to call
   * `startSession` (which would be WRONG here anyway: that creates a new
   * session instead of resuming the one the URL names).
   *
   * `sessionId` is supplied by the route wrapper (`SessionRouteProvider` in
   * App.tsx for `/session/:id`); `initialState` is the `SessionState`
   * that wrapper already fetched to resolve the owning campaign — reusing it
   * here avoids fetching that SAME state a second time just to seed
   * ("minimize duplicate independent session-state authorities"). It does
   * NOT mean this mount makes no network request at all: `seed()` below
   * always calls `startPolling()`, which fires its own immediate poll fetch
   * right after seeding, seeded-from-`initialState` or not — that first poll
   * tick is expected, ordinary behavior, not a duplicate of the seed. When
   * `initialState` is absent or stale (its `session_id` doesn't match
   * `sessionId`), this effect fetches it itself before seeding.
   *
   * Re-runs whenever `sessionId` changes — covering direct navigation from
   * one session to another WITHIN THE SAME CAMPAIGN, where `App.tsx`'s
   * `key={campaignId}` does not remount this provider — or when
   * `retryInitialization()` is called. `cancelled` plus the shared
   * `pollRequestIdRef` generation counter reject a stale response: if
   * `sessionId` changes again (or the provider unmounts) before a fetch
   * resolves, that resolution is discarded rather than overwriting the
   * newer session's state. `stopPolling()` in the cleanup guarantees the
   * previous session's 3-second interval never keeps ticking against the
   * wrong id once a new one takes over, whether the previous mount seeded
   * synchronously from `initialState` or via its own fetch.
   */
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    const requestId = ++pollRequestIdRef.current;

    const seed = (state: SessionState) => {
      if (cancelled || requestId !== pollRequestIdRef.current) return; // superseded
      setSession(sessionFromState(state));
      setSessionState(state);
      setIsActive(state.status === 'active' || state.status === 'paused');
      setStatus('ready');
      setMessage(null);
      startPolling(state.session_id);
    };

    if (initialState && initialState.session_id === sessionId) {
      seed(initialState);
    } else {
      setStatus('loading');
      setMessage(null);
      pollAbortRef.current?.abort();
      const controller = new AbortController();
      pollAbortRef.current = controller;
      sessionService
        .getSessionState(sessionId, { signal: controller.signal })
        .then(seed)
        .catch((err) => {
          if (cancelled || requestId !== pollRequestIdRef.current) return; // superseded
          if (err instanceof DOMException && err.name === 'AbortError') return; // intentionally cancelled
          const { status: nextStatus, message: nextMessage } = statusFromError(err);
          setStatus(nextStatus);
          setMessage(nextMessage);
        });
    }

    return () => {
      cancelled = true;
      stopPolling();
    };
    // initialState is intentionally excluded: SessionRouteProvider only
    // produces a new object when sessionId itself changes (its own effect is
    // keyed the same way), so including it would not change behavior here
    // but would make this effect's dependency reasoning less clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, initRetryToken]);

  /**
   * CampaignPage and SessionControls start through authenticated HTTP using
   * the campaign ID. Share duplicate in-flight requests, validate the returned
   * identity and seed the provider only while its route remains current.
   * Navigation/unmount abort and invalidate acknowledgments even if transport
   * cancellation is ignored; polling then resolves caller permissions.
   */
  const startSession = useCallback(async (cId: string, source: 'webapp' | 'discord'): Promise<Session> => {
    if (campaignId && cId !== campaignId) throw new Error('Cannot start a session for a different campaign');
    const inFlight = lifecycleInFlightRef.current;
    if (inFlight?.kind === 'start' && inFlight.key === cId) return inFlight.promise;
    const startCursor = campaignCursorRef.current;
    if (!startCursor || startCursor.campaign_id !== cId || typeof startCursor.is_session_active !== 'boolean') {
      const unavailable = new Error('Campaign state is not confirmed yet. Refresh and review it before starting a session.');
      setStatus('unavailable');
      setMessage(unavailable.message);
      throw unavailable;
    }

    const identity = providerIdentityRef.current;
    const realtimeRevisionAtStart = realtimeRevisionRef.current;
    const requestId = ++lifecycleRequestIdRef.current;
    const controller = new AbortController();
    lifecycleAbortRef.current?.abort();
    lifecycleAbortRef.current = controller;
    const isCurrent = () =>
      !controller.signal.aborted &&
      requestId === lifecycleRequestIdRef.current &&
      providerIdentityRef.current.campaignId === identity.campaignId &&
      providerIdentityRef.current.sessionId === identity.sessionId;

    setStatus('loading');
    setMessage(null);
    const action = (async () => {
      try {
        const started = await sessionService.startSession(cId, { source, expected_current_session_id: startCursor.current_session_id, expected_is_session_active: startCursor.is_session_active }, { signal: controller.signal });
        if (!isCurrent()) {
          const cancelled = new Error('Session start was cancelled');
          cancelled.name = 'AbortError';
          throw cancelled;
        }
        if (started.campaign_id !== cId || started.status !== 'active') {
          throw new Error('Session start response did not match the requested campaign');
        }
        if (realtimeRevisionRef.current !== realtimeRevisionAtStart) {
          // A newer campaign snapshot arrived while start was pending. It already reconciled
          // this route; do not let the older HTTP acknowledgment recreate stale local state.
          return started;
        }
        setSession(started);
        setSessionState({
          session_id: started.session_id,
          campaign_id: started.campaign_id,
          status: 'active',
          state: 'active',
          started_at: started.started_at,
          elapsed_minutes: 0,
          elapsed_seconds: 0,
          lifecycle_revision: started.lifecycle_revision,
          master_mode_enabled: false,
          can_pause: false,
          can_resume: false,
          paused_by_me: false,
        });
        setIsActive(true);
        setStatus('ready');
        setMessage(null);
        startPolling(started.session_id);
        return started;
      } catch (err) {
        if (isCurrent()) {
          if (!(err instanceof Error && err.name === 'AbortError')) {
            const { status: nextStatus, message: nextMessage } = statusFromError(err);
            setStatus(nextStatus);
            setMessage(nextMessage);
            console.error('Start session error:', nextMessage);
          }
        }
        throw err;
      } finally {
        if (requestId === lifecycleRequestIdRef.current) {
          lifecycleAbortRef.current = null;
          lifecycleInFlightRef.current = null;
        }
      }
    })();
    lifecycleInFlightRef.current = { kind: 'start', key: cId, promise: action };
    return action;
  }, [campaignId, session, startPolling, realtimeRevisionRef]);

  const forceRefresh = useCallback(async (sessionId: string, expectedRealtimeRevision?: number) => {
    lastRefreshRef.current = Date.now();
    await fetchState(sessionId, expectedRealtimeRevision);
  }, [fetchState]);

  const requireSessionRevision = useCallback(async (targetId: string, isCurrent: () => boolean): Promise<number> => {
    const current = sessionState;
    const revision = current?.session_id === targetId ? current.lifecycle_revision : undefined;
    if (Number.isSafeInteger(revision) && (revision as number) >= 0) return revision as number;
    // Fetch once to restore a versioned view, but require a separate human
    // action after that refresh; never retry the consequential command. The
    // action reservation is already installed before this await, so navigation
    // can cancel it and a late refresh cannot set errors on the next route.
    await forceRefresh(targetId).catch(() => undefined);
    if (!isCurrent()) throw Object.assign(new Error('Session action is no longer current'), { name: 'AbortError' });
    const unavailable = new Error('Session revision is unavailable. State was refreshed; review it before trying again.');
    setStatus('error');
    setMessage(unavailable.message);
    throw unavailable;
  }, [forceRefresh, sessionState]);


  /**
   * End current session. The persisted response is the acknowledgment; keep
   * the ended identity in the provider so controls and the timer stop at once.
   */
  const endSession = useCallback(async (summary?: string): Promise<Session> => {
    if (!session) throw new Error('No active session');
    const inFlight = lifecycleInFlightRef.current;
    if (inFlight?.kind === 'end' && inFlight.key === session.session_id) return inFlight.promise;

    const ending = session;
    // Reserve synchronously, before revision lookup can await a refresh. This
    // makes same-tick end calls share one promise and lets route cleanup abort
    // the request even when its version is not available yet.
    const identity = providerIdentityRef.current;
    const realtimeRevisionAtStart = realtimeRevisionRef.current;
    const requestId = ++lifecycleRequestIdRef.current;
    const controller = new AbortController();
    lifecycleAbortRef.current?.abort();
    lifecycleAbortRef.current = controller;
    const isCurrent = () =>
      !controller.signal.aborted &&
      requestId === lifecycleRequestIdRef.current &&
      providerIdentityRef.current.campaignId === identity.campaignId &&
      providerIdentityRef.current.sessionId === identity.sessionId;

    setStatus('loading');
    setMessage(null);
    stopPolling();
    const action = (async () => {
      try {
        const expectedRevision = await requireSessionRevision(ending.session_id, isCurrent);
        if (!isCurrent()) throw Object.assign(new Error('Session end was cancelled'), { name: 'AbortError' });
        const ended = await sessionService.endSession(ending.session_id, { expected_revision: expectedRevision, ...(summary !== undefined ? { session_summary: summary } : {}) }, { signal: controller.signal });
        if (!isCurrent()) {
          const cancelled = new Error('Session end was cancelled');
          cancelled.name = 'AbortError';
          throw cancelled;
        }
        if (ended.session_id !== ending.session_id || ended.campaign_id !== ending.campaign_id || ended.status !== 'ended') {
          throw new Error('Session end response did not match the current session');
        }
        if (realtimeRevisionRef.current !== realtimeRevisionAtStart) {
          // A newer snapshot has already reconciled this view; the lifecycle promise still
          // resolves, but the older end response cannot replace that projection.
          return ended;
        }
        const previousState = sessionState?.session_id === ending.session_id ? sessionState : null;
        setSession(ended);
        setSessionState(previousState ? {
          ...previousState,
          session_id: ended.session_id,
          campaign_id: ended.campaign_id,
          status: 'ended',
          state: 'ended',
          started_at: ended.started_at,
          lifecycle_revision: ended.lifecycle_revision,
          master_mode_enabled: false,
          can_pause: false,
          can_resume: false,
          paused_by_me: false,
        } : null);
        setIsActive(false);
        setStatus('ready');
        setMessage(null);
        stopPolling();
        return ended;
      } catch (err) {
        if (isCurrent()) {
          if (!(err instanceof Error && err.name === 'AbortError')) {
            const { status: nextStatus, message: fallbackMessage } = statusFromError(err);
            const nextMessage = getSessionErrorCode(err) === 'stale_state' ? getSessionErrorMessage(err, fallbackMessage) : (err instanceof Error && err.message.includes('revision is unavailable') ? err.message : fallbackMessage);
            setStatus(nextStatus);
            setMessage(nextMessage);
            console.error('End session error:', nextMessage);
            // A rejected or uncertain end needs a fresh server read; it may
            // have committed even if the response was lost.
            startPolling(ending.session_id);
          }
        }
        throw err;
      } finally {
        if (requestId === lifecycleRequestIdRef.current) {
          lifecycleAbortRef.current = null;
          lifecycleInFlightRef.current = null;
        }
      }
    })();
    lifecycleInFlightRef.current = { kind: 'end', key: ending.session_id, promise: action };
    return action;
  }, [session, sessionState, startPolling, stopPolling, requireSessionRevision]);

  /**
   * Manually refresh session state (immediate fetch, not waiting for poll interval).
   *
   * DEBOUNCE GUARD (500ms): see module header. This is for coalescing
   * DUPLICATE read/event triggers only (e.g. two WebSocket events arriving
   * within milliseconds of each other, or a WebSocket push that lands right
   * after a poll tick already refreshed the same state) — never for a
   * completed mutation's own refresh. See `forceRefresh` below for that case
   * (PRD001A R5a).
   */
  const refreshState = useCallback(async () => {
    if (!session) return;
    const now = Date.now();
    if (now - lastRefreshRef.current < 500) return;
    lastRefreshRef.current = now;
    await fetchState(session.session_id);
  }, [session, fetchState]);

  /**
   * Unconditional authoritative refresh after a COMPLETED mutation (pause,
   * resume, or a resync after a conflict) — PRD001A R5a.
   *
   * WHY this exists separately from `refreshState`: that function's 500ms
   * debounce guard exists to coalesce genuine DUPLICATE triggers for the
   * same already-current state (a pause/resume REST call and the backend's
   * own `session.paused`/`session.resumed` WebSocket broadcast both calling
   * `refreshState` within milliseconds of each other). But the guard keys
   * only on elapsed time, not on whether the state actually changed — so a
   * pause immediately followed by a resume (both completing inside that same
   * 500ms window, e.g. two fast clicks or two fast REST round-trips) hit the
   * SAME "already refreshed recently" guard and the second, genuinely NEW
   * result was silently dropped: the UI kept showing the pre-resume
   * (paused/Resume) display even though the resume had actually succeeded.
   * A completed mutation always has a real new server outcome to show — it
   * must never be coalesced away.
   *
   * `forceRefresh` calls `fetchState` directly (bypassing the 500ms check
   * entirely) so every completed mutation's outcome is always fetched and
   * applied, but it ALSO stamps `lastRefreshRef` first — so a WebSocket
   * event that lands a moment later for the SAME action (the duplicate case
   * the guard exists for) still gets coalesced by `refreshState` as before.
   * `fetchState`'s own request-generation counter (`pollRequestIdRef`) is
   * what actually guarantees an older in-flight response (e.g. a slow poll
   * tick that started before this call) can never overwrite this call's
   * newer result — see `fetchState` above.
   */
  /**
   * Pause the active session.
   *
   * Real policy (backend-enforced, PRD001A 001A.5): ANY verified current
   * campaign participant may pause an 'active' session — not just the GM.
   * This context does not gate the call itself; it trusts the server's
   * response (`SessionControls` renders its button from the polled
   * `can_pause` flag, never a client-side role guess). Calls
   * `SessionService.pauseSession()` then an unconditional `forceRefresh()`
   * (PRD001A R5a) to sync local state with the server response.
   *
   * On a 409 conflict (e.g. `session_not_active`/`session_ended`/
   * `already_paused_by_other`), the thrown message is replaced with an
   * honest, code-specific one via `getSessionErrorMessage`, and state is
   * refreshed immediately so the caller's `can_pause`/`can_resume` flags
   * reflect what actually happened server-side rather than staying stale.
   */
  const mutatePauseState = useCallback(async (kind: 'pause' | 'resume', targetId: string): Promise<void> => {
    const identity = providerIdentityRef.current;
    const realtimeRevisionAtStart = realtimeRevisionRef.current;
    const cancelled = () => Object.assign(new Error('Session action is no longer current'), { name: 'AbortError' });
    if (identity.sessionId && identity.sessionId !== targetId) throw cancelled();
    // Install the generation/controller before any version-refresh await so a
    // route change invalidates the action before it can submit a stale target.
    const requestId = ++lifecycleRequestIdRef.current;
    const controller = new AbortController();
    lifecycleAbortRef.current?.abort();
    lifecycleAbortRef.current = controller;
    const isCurrent = () => !controller.signal.aborted &&
      requestId === lifecycleRequestIdRef.current &&
      providerIdentityRef.current.campaignId === identity.campaignId &&
      providerIdentityRef.current.sessionId === identity.sessionId;
    setMessage(null);
    try {
      const expectedRevision = await requireSessionRevision(targetId, isCurrent);
      if (!isCurrent()) throw cancelled();
      if (kind === 'pause') await sessionService.pauseSession(targetId, expectedRevision, { signal: controller.signal });
      else await sessionService.resumeSession(targetId, expectedRevision, { signal: controller.signal });
      if (!isCurrent()) throw cancelled();
      // Every CURRENT mutation refreshes, even inside the 500ms event debounce.
      await forceRefresh(targetId, realtimeRevisionAtStart);
      if (!isCurrent()) throw cancelled();
    } catch (err) {
      if (!isCurrent()) throw cancelled();
      if (err instanceof Error && err.name === 'AbortError') throw err;
      const { status: nextStatus } = statusFromError(err);
      const honestMessage = err instanceof Error && err.message.includes('revision is unavailable') ? err.message : getSessionErrorMessage(err, `Failed to ${kind} session`);
      setStatus(nextStatus);
      setMessage(honestMessage);
      console.error(`${kind === 'pause' ? 'Pause' : 'Resume'} session error:`, honestMessage);
      if (err instanceof ApiError && err.kind === 'conflict') {
        await forceRefresh(targetId, realtimeRevisionAtStart).catch(() => undefined);
      }
      throw err;
    } finally {
      if (requestId === lifecycleRequestIdRef.current) lifecycleAbortRef.current = null;
    }
  }, [forceRefresh, requireSessionRevision]);

  // Membership and pause-owner authority remain server enforced. Aborting a
  // request only stops this view from applying it; it cannot undo a server save.
  const pauseSession = useCallback((targetId: string) => mutatePauseState('pause', targetId), [mutatePauseState]);
  const resumeSession = useCallback((targetId: string) => mutatePauseState('resume', targetId), [mutatePauseState]);

  const setMasterMode = useCallback(async (targetId: string, enabled: boolean): Promise<void> => {
    const identity = providerIdentityRef.current;
    const realtimeRevisionAtStart = realtimeRevisionRef.current;
    const cancelled = () => Object.assign(new Error('Session action is no longer current'), { name: 'AbortError' });
    if (identity.sessionId && identity.sessionId !== targetId) throw cancelled();
    // As with pause/resume, reserve before awaiting any missing revision fetch.
    const requestId = ++lifecycleRequestIdRef.current;
    const controller = new AbortController();
    lifecycleAbortRef.current?.abort();
    lifecycleAbortRef.current = controller;
    const isCurrent = () => !controller.signal.aborted &&
      requestId === lifecycleRequestIdRef.current &&
      providerIdentityRef.current.campaignId === identity.campaignId &&
      providerIdentityRef.current.sessionId === identity.sessionId;
    setMessage(null);
    try {
      const expectedRevision = await requireSessionRevision(targetId, isCurrent);
      if (!isCurrent()) throw cancelled();
      await sessionService.setMasterMode(targetId, enabled, expectedRevision, { signal: controller.signal });
      if (!isCurrent()) throw cancelled();
      await forceRefresh(targetId, realtimeRevisionAtStart);
      if (!isCurrent()) throw cancelled();
    } catch (err) {
      if (!isCurrent()) throw cancelled();
      if (err instanceof Error && err.name === 'AbortError') throw err;
      const { status: nextStatus } = statusFromError(err);
      const honestMessage = err instanceof Error && err.message.includes('revision is unavailable') ? err.message : getSessionErrorMessage(err, 'Failed to update session mode');
      setStatus(nextStatus);
      setMessage(honestMessage);
      if (err instanceof ApiError && err.kind === 'conflict') {
        await forceRefresh(targetId, realtimeRevisionAtStart).catch(() => undefined);
      }
      throw err;
    } finally {
      if (requestId === lifecycleRequestIdRef.current) lifecycleAbortRef.current = null;
    }
  }, [forceRefresh, requireSessionRevision]);

  const clearError = useCallback(() => {
    setStatus('idle');
    setMessage(null);
  }, []);

  // Cancel polling (interval + any in-flight fetch) on unmount, or if the
  // campaignId prop itself changes on an already-mounted instance (defensive
  // — the intended usage keys this component by campaignId at the call site
  // in App.tsx, so a change normally means a fresh instance; this makes the
  // component correct standalone too and satisfies "polling interval is
  // cancelled on campaign change" without relying solely on the key remount).
  useLayoutEffect(() => {
    campaignCursorRef.current = null;
    setCampaignCursor(null);
  }, [campaignId]);

  useLayoutEffect(() => {
    return () => {
      lifecycleRequestIdRef.current += 1;
      lifecycleAbortRef.current?.abort();
      lifecycleAbortRef.current = null;
      lifecycleInFlightRef.current = null;
      stopPolling();
    };
  }, [campaignId, sessionId, stopPolling]);

  const applyRealtimeCampaignCursor = useCallback((cursor: CampaignMutationCursor | null) => {
    if (cursor === null) {
      campaignCursorRef.current = null;
      setCampaignCursor(null);
      return;
    }
    // Transport ordering and mutation revision are separate domains. Validate
    // the cursor before publishing it, and ignore a delayed lower campaign
    // revision so a stale event cannot re-enable a start intent.
    if (campaignId && cursor.campaign_id !== campaignId) return;
    if (!(cursor.current_session_id === null || (typeof cursor.current_session_id === 'string' && cursor.current_session_id.length > 0)) ||
        typeof cursor.is_session_active !== 'boolean' || !Number.isSafeInteger(cursor.server_revision) || cursor.server_revision < 0) return;
    const previous = campaignCursorRef.current;
    if (previous?.campaign_id === cursor.campaign_id && cursor.server_revision < previous.server_revision) return;
    campaignCursorRef.current = cursor;
    setCampaignCursor(cursor);
  }, [campaignId]);

  const applyRealtimeState = useCallback((state: SessionState | null) => {
    realtimeRevisionRef.current += 1;
    pollRequestIdRef.current += 1;
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    if (!state) {
      stopPolling();
      setSession(null); setSessionState(null); setIsActive(false); setStatus('empty'); setMessage(null);
      return;
    }
    setSession(sessionFromState(state));
    setSessionState(state);
    setIsActive(state.status === 'active' || state.status === 'paused');
    setStatus('ready'); setMessage(null);
    if (state.status === 'ended') stopPolling();
  }, [stopPolling]);

  const value: SessionContextType = {
    session,
    sessionState,
    isActive,
    status,
    message,
    campaignCursor,
    isLoading: status === 'loading',
    startSession,
    endSession,
    refreshState,
    pauseSession,
    resumeSession,
    setMasterMode,
    clearError,
    retryInitialization,
    applyRealtimeState,
    applyRealtimeCampaignCursor
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

/**
 * Hook to use session context
 *
 * @returns SessionContextType
 * @throws Error if used outside SessionProvider
 */
export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};
